import moment from "moment";
import { useState } from "react";
import RangeSelectors from "../../../../parts/filters/range-selectors";

const CUSTOM_RANGE = /^(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/;
const PRIOR_YEARS = 5;

/**
 * The window a selector covers, as moments — the GUI twin of msb-api's
 * windowFromSelector (sc-45975), used only for display (labels, the
 * period band's date range). null for "lifetime" / unrecognised.
 *   N (months): current month + N - 1 before it; 1 = month to date
 *   "ytd" · "year-YYYY" · "YYYY-MM-DD_YYYY-MM-DD" (inclusive)
 */
export const periodWindow = (selector) => {
  if (selector == null || selector === "lifetime") return null;
  if (selector === "ytd") {
    return { since: moment().startOf("year"), until: moment() };
  }
  const year = /^year-(\d{4})$/.exec(selector)?.[1];
  if (year) {
    const since = moment(`${year}-01-01`);
    // The current year is year-to-date (matches msb-api's windowFromSelector).
    const endOfYear = since.clone().endOf("year");
    return { since, until: moment.min(endOfYear, moment()) };
  }
  const custom = CUSTOM_RANGE.exec(selector);
  if (custom) {
    const since = moment(custom[1], "YYYY-MM-DD", true);
    const until = moment(custom[2], "YYYY-MM-DD", true);
    return since.isValid() && until.isValid() && !since.isAfter(until)
      ? { since, until }
      : null;
  }
  const months = Number(selector);
  if (!Number.isFinite(months)) return null;
  return {
    since: moment()
      .startOf("month")
      .subtract(months - 1, "months"),
    until: moment(),
  };
};

// "for {{range}}" prose — the period band, Plans tooltips, Growth badges.
const MONTH_KEYS = { 3: "3month", 6: "6month", 12: "1year" };
export const periodLabel = (t, selector) => {
  if (String(selector) === "1") return t("income.range.mtd");
  if (selector === "ytd") return t("income.range.ytd");
  if (MONTH_KEYS[selector]) return t(`rangeSelectors.${MONTH_KEYS[selector]}`);
  const window = periodWindow(selector);
  if (!window) return t("rangeSelectors.lifetime");
  if (/^year-/.test(selector)) return window.since.format("YYYY");
  return formatPeriodRange(window);
};

export const formatPeriodRange = ({ since, until }) =>
  since.year() === until.year()
    ? `${since.format("MMM D")} – ${until.format("MMM D, YYYY")}`
    : `${since.format("MMM D, YYYY")} – ${until.format("MMM D, YYYY")}`;

/**
 * Income's period picker: preset buttons, a prior-year dropdown and a
 * custom date range (native date inputs). Every choice becomes one
 * `selector` string, so the controller and loaders pass it through as-is.
 */
const PeriodControls = ({ selector, onSelect, t }) => {
  const custom = CUSTOM_RANGE.exec(selector || "");
  const [showCustom, setShowCustom] = useState(!!custom);
  const [from, setFrom] = useState(custom?.[1] || "");
  const [to, setTo] = useState(custom?.[2] || "");
  const today = moment().format("YYYY-MM-DD");
  const thisYear = moment().year();
  const years = Array.from({ length: PRIOR_YEARS + 1 }, (_, i) => thisYear - i);
  // Numeric selectors can arrive as "3" from the query string.
  const selected = Number.isFinite(Number(selector))
    ? Number(selector)
    : selector;

  return (
    <div className="period-controls">
      <RangeSelectors
        selectedValue={custom ? null : selected}
        onClick={onSelect}
        selectors={[
          { text: t("income.range.mtdButton"), value: 1 },
          { text: t("rangeSelectors.3month"), value: 3 },
          { text: t("rangeSelectors.6month"), value: 6 },
          { text: t("rangeSelectors.1year"), value: 12 },
          { text: t("income.range.ytdButton"), value: "ytd" },
          { text: t("rangeSelectors.lifetime"), value: "lifetime" },
        ]}
      />
      <select
        value={/^year-\d{4}$/.test(selector || "") ? selector : ""}
        onChange={(e) => e.target.value && onSelect(e, e.target.value)}
        aria-label={t("income.range.year")}
      >
        <option value="">{t("income.range.year")}</option>
        {years.map((year) => (
          <option key={year} value={`year-${year}`}>
            {year}
          </option>
        ))}
      </select>
      <button
        type="button"
        data-button-type={custom || showCustom ? "primary" : "secondary"}
        onClick={() => setShowCustom((open) => !open)}
      >
        {t("income.range.custom")}
      </button>
      {showCustom && (
        <form
          className="period-controls__custom"
          onSubmit={(e) => onSelect(e, `${from}_${to}`)}
        >
          <input
            type="date"
            value={from}
            max={to || today}
            onChange={(e) => setFrom(e.target.value)}
            aria-label={t("income.range.from")}
            required
          />
          <span>–</span>
          <input
            type="date"
            value={to}
            min={from || undefined}
            max={today}
            onChange={(e) => setTo(e.target.value)}
            aria-label={t("income.range.to")}
            required
          />
          <button
            type="submit"
            data-button-type="primary"
            disabled={!from || !to || from > to}
          >
            {t("income.range.apply")}
          </button>
        </form>
      )}
    </div>
  );
};

export default PeriodControls;
